import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/1dde86ed/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const ResourceCard = makeShortcode("ResourceCard");
const MdxIcon = makeShortcode("MdxIcon");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageDescription mdxType="PageDescription">
      <p>{`You have established a need for a chatbot based on extensive market validation and user research. How should you begin to think about designing your conversation?`}</p>
    </PageDescription>
    <AnchorLinks mdxType="AnchorLinks">
  <AnchorLink mdxType="AnchorLink">Conversational writing</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Resources</AnchorLink>
  <AnchorLink mdxType="AnchorLink">References</AnchorLink>
    </AnchorLinks>
    <h2>{`Conversational writing`}</h2>
    <h3>{`Tailoring the conversation to your users`}</h3>
    <p>{`Be sure to consider the tone of your bot. Tone should depend on the audience and the context in which the user will be engaging in a conversation. For example, a healthcare professional will require a different tone than the end user of a financial banking bot.`}</p>
    <p>{`Each bot requires a uniquely tailored experience. While in many cases the following principles will suffice, ensure that your bot’s tone is not distracting or at odds with users’ expectations.`}</p>
    <h4>{`In general, bots should:`}</h4>
    <ul>
      <li parentName="ul">{`Use simple language that is easy to understand`}</li>
      <li parentName="ul">{`Be helpful and agreeable, but not forced or insincere`}</li>
      <li parentName="ul">{`Confirm its understanding of user inputs when appropriate (i.e., by paraphrasing).`}</li>
      <li parentName="ul">{`Provide help or a menu of options when a user is stuck`}</li>
    </ul>
    <h4>{`Typically, bots should not:`}</h4>
    <ul>
      <li parentName="ul">{`Attempt to seem like a real human`}</li>
      <li parentName="ul">{`Fail to provide an answer without recourse`}</li>
      <li parentName="ul">{`Be pushy or send unsolicited messages`}</li>
    </ul>
    <p>{`See the voice and tone guide from `}<a parentName="p" {...{
        "href": "http://www.carbondesignsystem.com/guidelines/content/general"
      }}>{`Carbon`}</a>{` for additional writing guidance.`}</p>
    <h3>{`Conversation design principles for bots`}</h3>
    <p>{`Ensure the success of your bot by following these principles:`}</p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Introduce your bot.`}</strong>{` Be crisp and clear about the identity of your bot, the value it can provide and why the user should trust it. Make it clear to your users that they are chatting with bot, not a real human. And, be sure to provide clear next steps.`}<ul parentName="li">
          <li parentName="ul">{`Is your bot setting the right expectations?`}</li>
          <li parentName="ul">{`Has your bot set the boundaries of what it can do?`}</li>
        </ul></li>
      <li parentName="ol"><strong parentName="li">{`Orient the user.`}</strong>{` Inform the user about what they can do, ask, or find. Don’t assume users will understand the scope of topics that can be discussed.`}</li>
      <li parentName="ol"><strong parentName="li">{`Be proactive.`}</strong>{` Suggest options (structured responses) as effective inputs that will help the user get what they are looking for or trying to do. Actively identify scenarios where guidance might be needed for users that don’t know what to ask or how to ask it.`}</li>
      <li parentName="ol"><strong parentName="li">{`Decrease ambiguity.`}</strong>{` Confirm user intents that have significant consequences by repeating what is about to happen (“To confirm, you want to move \$300.00 to Checking?”). Summarize complex input from the user to confirm the bot’s understanding. Where possible, use structured responses to prevent dead-ends, unknowns, or chats that go off in tangents.`}</li>
      <li parentName="ol"><strong parentName="li">{`Provide clear actions.`}</strong>{` Provide a menu of structured responses narrow the scope of the input. Don’t overwhelm the user with too many choices.`}</li>
      <li parentName="ol"><strong parentName="li">{`Guide the user to their goal.`}</strong>{` Ensure successful interactions by providing guidance and feedback during complex processes. Notify the user how long a process might take, or if they are on the right track. Where appropriate allow users to cancel or restart longer processes.`}</li>
      <li parentName="ol"><strong parentName="li">{`Get feedback.`}</strong>{` Collect user feedback to improve your bot’s relevance and accuracy over time.`}</li>
    </ol>
    <h2>{`Resources`}</h2>
    <Row className="resource-card-group" mdxType="Row">
  <Column colLg={4} colMd={4} noGutterSm mdxType="Column">
    <ResourceCard subTitle="IBM Design for AI: Conversation" href="http://ai-design.eu-de.mybluemix.net/design/ai/conversation/planning" mdxType="ResourceCard">
      <MdxIcon name="bee" mdxType="MdxIcon" />
    </ResourceCard>
  </Column>
    </Row>
    <h2>{`References`}</h2>
    <ul>
      <li parentName="ul">{`Raluca Budiu, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/chatbots/"
        }}>{`The User Experience of Chatbots`}</a>{`, (Nielsen Norman Group, 2018)`}</li>
      <li parentName="ul">{`Adam Cutler, Zach Causey and Lawrence Humphrey, IBM Design Conversational Experience Guide (2017)`}</li>
      <li parentName="ul">{`Erika Hall, `}<a parentName="li" {...{
          "href": "https://abookapart.com/products/conversational-design"
        }}>{`Conversational Design, A Book Apart`}</a>{` (A Book Apart, 2018)`}</li>
      <li parentName="ul">{`William R. Miller and Stephen Rollnick, `}<a parentName="li" {...{
          "href": "https://www.guilford.com/books/Motivational-Interviewing/Miller-Rollnick/9781609182274"
        }}>{`Motivational Interviewing: Helping People Change`}</a>{` (Guildford Press, 2012)`}</li>
      <li parentName="ul">{`Robert J Moore and Arar Raphael, `}<a parentName="li" {...{
          "href": "http://www.morganclaypoolpublishers.com/catalog_Orig/product_info.php?products_id=1414"
        }}>{`Conversational UX Design: A Practitioner’s Guide to the Natural Conversation Framework`}</a>{` (Association for Computing Machinery, 2019)`}</li>
      <li parentName="ul">{`Cathy Pearl, `}<a parentName="li" {...{
          "href": "http://shop.oreilly.com/product/0636920050056.do"
        }}>{`Designing Voice User Interfaces: Principles of Conversational Experiences`}</a>{` (O’Reilly, 2016)`}</li>
      <li parentName="ul">{`Amit Shevat, `}<a parentName="li" {...{
          "href": "http://shop.oreilly.com/product/0636920057741.do"
        }}>{`Designing Bots: Creating Conversational Experiences`}</a>{` (O’Reilly, 2017)`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      